import { useMemo } from 'react';
import type { DjayVariant } from '../../mascot';
import type { VariantUnlockInfo } from '../../mascot/useVariantUnlocks';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';

/** An achievement enriched with its live unlock + progress state. */
export interface AchievementWithProgress {
  id: string;
  name: string;
  isUnlocked: boolean;
  djayVariantUnlocked?: DjayVariant;
  progress?: {
    current: number;
    max: number;
    label: string;
  };
}

/** The single closest-to-done locked collectible the learner can chase next. */
export interface NextReward {
  name: string;
  pct: number;
  current: number;
  max: number;
  label: string;
  variantId: DjayVariant | null;
}
export interface CollectionProgress {
  /** De-duplicated count of distinct unlocked collectibles. */
  totalUnlocked: number;
  /** De-duplicated count of all distinct collectibles. */
  totalCollectibles: number;
  /** Rounded percentage 0–100. */
  progressPercentage: number;
  /** Standalone variants not owned by any achievement (default + secrets). */
  standaloneVariants: VariantUnlockInfo[];
  /** Closest-to-done locked reward, or null when everything is unlocked. */
  nextReward: NextReward | null;
  /** Achievements currently unlocked (used by the per-track "Prestaties" tile). */
  unlockedAchievementsCount: number;
  /** Total achievement cards (used by the per-track "Prestaties" tile). */
  totalAchievementsCount: number;
}

/**
 * useCollectionProgress — collection-domain selector that derives the overall
 * "Verzameling compleet" tally and the next-reward teaser.
 *
 * DDD note: this collectible-domain reasoning (de-duplicating achievement-owned
 * variants vs. standalone ones, the union tally, and the closest-to-done
 * candidate selection) previously lived inline in `CollectionPage`. Extracting
 * it keeps the page declarative and makes the math independently testable.
 *
 * Most variants are GRANTED BY an achievement (`achievement.djayVariantUnlocked`),
 * so counting both tracks naively would double-count the same reward. We count
 * every achievement once, plus only the "standalone" variants that no
 * achievement owns (e.g. the always-on default + the secret 'goud' easter-egg).
 */
export function useCollectionProgress(mascotVariants: VariantUnlockInfo[], achievementsWithProgress: AchievementWithProgress[]): CollectionProgress {
  const achievementOwnedVariants = useMemo(() => new Set(ACHIEVEMENTS.map(a => a.djayVariantUnlocked).filter((v): v is DjayVariant => !!v)), []);
  const standaloneVariants = useMemo(() => mascotVariants.filter(v => !achievementOwnedVariants.has(v.id)), [mascotVariants, achievementOwnedVariants]);
  return useMemo(() => {
    const unlockedAchievementsCount = achievementsWithProgress.filter(a => a.isUnlocked).length;
    const totalAchievementsCount = achievementsWithProgress.length;
    const totalCollectibles = totalAchievementsCount + standaloneVariants.length;
    const totalUnlocked = unlockedAchievementsCount + standaloneVariants.filter(v => v.unlocked).length;
    const progressPercentage = totalCollectibles > 0 ? Math.round(totalUnlocked / totalCollectibles * 100) : 0;

    // Mirror of the Voortgang GhostTrophy: surface the single closest-to-done
    // locked collectible so the learner always has a concrete "next reward".
    const candidates: NextReward[] = [];
    standaloneVariants.forEach(v => {
      if (!v.unlocked && v.progress.max > 0) {
        candidates.push({
          name: v.name,
          pct: v.progress.current / v.progress.max,
          current: v.progress.current,
          max: v.progress.max,
          label: v.progress.label,
          variantId: v.id
        });
      }
    });
    achievementsWithProgress.forEach(a => {
      if (!a.isUnlocked && a.progress && a.progress.max > 0) {
        candidates.push({
          name: a.name,
          pct: a.progress.current / a.progress.max,
          current: a.progress.current,
          max: a.progress.max,
          label: a.progress.label,
          variantId: a.djayVariantUnlocked ?? null
        });
      }
    });
    const nextReward = candidates.filter(c => c.pct < 1).sort((a, b) => b.pct - a.pct)[0] ?? null;
    return {
      totalUnlocked,
      totalCollectibles,
      progressPercentage,
      standaloneVariants,
      nextReward,
      unlockedAchievementsCount,
      totalAchievementsCount
    };
  }, [achievementsWithProgress, standaloneVariants]);
}